import { useEffect, useState } from "react";
import type { Routines as RoutinesData } from "../api.ts";
import { C, mono, sans, todayISO } from "../theme.ts";
import { Markdown } from "../components/Markdown.tsx";

// Three named lists. Ticked within the day and never recorded.
//
// ───────────────────────────────────────────────────────────────────────────
// Read this before changing anything in this file.
//
// The ticks live in localStorage under today's date and are dropped the moment
// the date changes. There is no server call here, no completions table behind
// it, and there must never be one — not "just for sync", not "just so the tick
// survives a reload on another device".
//
// The project rule is that rhythms and routines are never tracked: no streak,
// no history, no count, no "days since", no completion percentage, ever. A
// tick that resets at midnight and is written down nowhere is an affordance
// within the day. The moment it persists past midnight, this has broken the
// rule it was built under.
//
// So: no counts in the copy either. No "2 of 4". No progress bar. No empty
// state telling him he has not started.
//
// `detail` is read-only prose hanging off a line — the ordered fallbacks
// behind an eating line, where item 1 is the default and the ones under it are
// what you make when the one above is not possible. It is shown when the name
// is tapped and it is not state: nothing records which one he took, nothing is
// written back, and it does not touch the tick or the midnight reset.
// ───────────────────────────────────────────────────────────────────────────

const KEY = "mc.routines";

type Ticks = Record<string, true>;

function read(date: string): Ticks {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "null");
    // Anything not stamped with today is not today's. It is dropped from the
    // device rather than merely ignored — leaving yesterday lying around is
    // how somebody later notices it is there and decides it could be useful.
    if (!raw || raw.date !== date) {
      localStorage.removeItem(KEY);
      return {};
    }
    return raw.ticks ?? {};
  } catch {
    localStorage.removeItem(KEY);
    return {};
  }
}

function write(date: string, ticks: Ticks) {
  try {
    localStorage.setItem(KEY, JSON.stringify({ date, ticks }));
  } catch {
    // A browser refusing storage means the ticks last as long as the tab.
    // That is a smaller loss than anywhere else in this app.
  }
}

const SLOTS = ["morning", "afternoon", "evening"] as const;
type Slot = (typeof SLOTS)[number];

/** Which list the day is currently in. The other two are one tap away. */
export function slotNow(at: Date = new Date()): Slot {
  const hour = Number(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/Berlin", hour: "2-digit", hour12: false,
    }).format(at),
  );
  if (hour < 12) return "morning";
  if (hour < 18) return "afternoon";
  return "evening";
}

export function Routines({ data }: { data: RoutinesData }) {
  const [date, setDate] = useState(todayISO());
  const [ticks, setTicks] = useState<Ticks>(() => read(todayISO()));
  const [open, setOpen] = useState<Slot>(slotNow());
  // Which line is showing its detail. One at a time, and it is not remembered
  // anywhere — closing the app closes it.
  const [reading, setReading] = useState<string | null>(null);

  // A phone left on overnight would otherwise still be showing last night's
  // ticks over this morning's list.
  useEffect(() => {
    const check = () => {
      const now = todayISO();
      if (now !== date) {
        setDate(now);
        setTicks({});
        write(now, {});
        setOpen(slotNow());
        setReading(null);
      }
    };
    const timer = setInterval(check, 60_000);
    document.addEventListener("visibilitychange", check);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", check);
    };
  }, [date]);

  const toggle = (id: string) => {
    setTicks((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = true;
      write(date, next);
      return next;
    });
  };

  const list = data[open] ?? [];

  return (
    <div style={{ marginTop: 26 }}>
      {/* A menu, not a wall of checkboxes: one slot showing, the others named. */}
      <div style={{ display: "flex", gap: 14, alignItems: "baseline" }}>
        {SLOTS.map((s) => (
          <button
            key={s}
            onClick={() => setOpen(s)}
            style={{
              fontFamily: mono, fontSize: 11, letterSpacing: 1,
              color: open === s ? C.inkSoft : C.inkGhost,
              background: "none", border: "none", padding: 0, cursor: "pointer",
              borderBottom: `1px solid ${open === s ? C.ruleStrong : "transparent"}`,
            }}
          >
            {s.toUpperCase()}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 10 }}>
        {list.map((r) => {
          const on = !!ticks[r.id];
          const openHere = reading === r.id;
          const name = {
            fontSize: 13.5, lineHeight: 1.4,
            color: on ? C.inkGhost : C.ink,
            textDecoration: on ? "line-through" : "none",
          } as const;
          return (
            <div key={r.id} style={{ borderBottom: `1px solid ${C.rule}` }}>
              <div
                onClick={() => toggle(r.id)}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "9px 2px", cursor: "pointer",
                }}
              >
                <span
                  style={{
                    width: 15, height: 15, borderRadius: 4, flexShrink: 0,
                    border: `1px solid ${on ? C.inkSoft : C.ruleStrong}`,
                    background: on ? C.inkSoft : "transparent",
                  }}
                />
                {/* A line with something to read opens on its name and ticks
                    everywhere else. The dotted underline is the footer's, and
                    it says "this one opens", not "this one matters more". */}
                {r.detail ? (
                  <button
                    onClick={(e) => { e.stopPropagation(); setReading(openHere ? null : r.id); }}
                    style={{
                      // `font` is a shorthand and resets size, family, weight
                      // and line-height together, so it goes before the spread
                      // rather than after it — after, it quietly threw away the
                      // 13.5px and the line rendered a size larger than the
                      // ones either side of it.
                      font: "inherit",
                      ...name,
                      fontFamily: sans,
                      background: "none", border: "none",
                      padding: 0, textAlign: "left", cursor: "pointer",
                      borderBottom: `1px dotted ${C.ruleStrong}`,
                    }}
                  >{r.name}</button>
                ) : (
                  <span style={name}>{r.name}</span>
                )}
              </div>

              {/* Read down until one works, then stop. There is no control in
                  here: nothing to pick, nothing to submit, nothing that
                  records which one he took. */}
              {openHere && r.detail && (
                <div style={{
                  background: C.cardQuiet, borderRadius: 12, border: `1px solid ${C.rule}`,
                  padding: "4px 14px 10px", margin: "0 0 10px 25px",
                }}>
                  <Markdown text={r.detail} />
                  <button
                    onClick={() => setReading(null)}
                    style={{
                      fontFamily: mono, fontSize: 11, color: C.inkFaint,
                      background: "none", border: "none", padding: 0, cursor: "pointer",
                    }}
                  >close</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
